import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useEasykitDriverStore } from '@/stores/easykitDriver';
import { uploadEasykitPhoto, listEasykitPhotos, deleteEasykitPhoto } from '@/api/easykit';
import type { TaskPhoto } from '@/api/easykit';
import type { EasykitDriverTask } from '@/types/odoo';
import type { PhotoItem } from '@/types/odoo';
import { compressImage } from '@/utils/imageCompression';
import { uploadPool } from '@/utils/uploadPool';
import { describeUploadFailures } from '@/utils/uploadFeedback';
import { TYPE_PILL } from '@/utils/easykit';
import { PhotoLightbox } from '@/components/ui/PhotoLightbox';
import { PhotoGridThumb } from '@/components/ui/PhotoGridThumb';
import { isPdfPhoto } from '@/utils/photoFiles';
import { StackedPhotoUploadSplitControls } from '@/components/ui/PhotoUploadSplitControls';

const TAG_CONFIG: Record<number, { label: string; cls: string }> = {
  7: { label: 'Voormiddag', cls: 'bg-green-100 text-green-800' },
  6: { label: 'Namiddag', cls: 'bg-blue-100 text-blue-800' },
  12: { label: 'Afhaling', cls: 'bg-amber-100 text-amber-800' },
};

const TYPE_COLORS: Record<string, { bg: string; border: string }> = {
  druktest: { bg: 'bg-red-50', border: 'border-l-red-500' },
  levering: { bg: 'bg-blue-50', border: 'border-l-blue-500' },
  feestdag: { bg: 'bg-purple-50', border: 'border-l-purple-500' },
  other: { bg: 'bg-gray-50', border: 'border-l-gray-400' },
};


function taskPhotoToPhotoItem(photo: TaskPhoto): PhotoItem {
  return {
    key: photo.key,
    url: photo.url,
    filename: photo.filename,
    lastModified: photo.lastModified || null,
    size: photo.size,
  };
}

export function EasykitDriverTaskDetail() {
  const { taskId: taskIdParam } = useParams<{ taskId: string }>();
  const taskId = Number(taskIdParam);
  const navigate = useNavigate();

  const tasks = useEasykitDriverStore((s) => s.tasks);
  const fetchTasks = useEasykitDriverStore((s) => s.fetchTasks);

  // Find task in store (already fetched by driver view)
  const [task, setTask] = useState<EasykitDriverTask | null>(null);

  useEffect(() => {
    const found = tasks.find((t) => t.id === taskId);
    if (found) {
      setTask(found);
    } else {
      // If not in store, fetch tasks
      fetchTasks();
    }
  }, [taskId, tasks, fetchTasks]);

  useEffect(() => {
    if (!task) {
      const found = tasks.find((t) => t.id === taskId);
      if (found) setTask(found);
    }
  }, [tasks, taskId, task]);

  // Photo state
  const [photos, setPhotos] = useState<TaskPhoto[]>([]);
  const [photosLoading, setPhotosLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const loadPhotos = useCallback(async () => {
    if (!task) return; // Wait until task is loaded
    setPhotosLoading(true);
    try {
      const projName = task.projectName || undefined;
      const taskType = task.taskType === 'druktest' ? 'druktest' : task.taskType === 'levering' ? 'levering' : 'overige';
      const data = await listEasykitPhotos(taskId, projName, taskType);
      setPhotos(data.photos);
    } catch {
      // Silent fail — show empty
    } finally {
      setPhotosLoading(false);
    }
  }, [taskId, task]);

  useEffect(() => {
    loadPhotos();
  }, [loadPhotos]);

  const handleUpload = async (files: FileList) => {
    if (!files || files.length === 0) return;
    if (!task) return;

    const fileList = Array.from(files);
    setUploading(true);
    setUploadError(null);
    setUploadProgress({ current: 0, total: fileList.length });

    const projName = task.projectName || `project-${taskId}`;
    const taskType = task.taskType === 'druktest' ? 'druktest' : task.taskType === 'levering' ? 'levering' : 'overige';

    // ADR-026: uploads go through the shared pool, which caps in-flight streams
    // globally at 2. The old local `BATCH_SIZE = 4` ignored that cap; six
    // parallel streams on a van's mobile uplink is exactly how an installer lost
    // photos to ERR_STREAM_PREMATURE_CLOSE on 2026-05-08.
    const { succeeded, failed, failures } = await uploadPool(fileList, async (file) => {
      const compressed = await compressImage(file);
      await uploadEasykitPhoto(taskId, compressed, projName, taskType);
      setUploadProgress((prev) => ({ ...prev, current: prev.current + 1 }));
    }, 2);

    await loadPhotos();
    setUploading(false);
    setUploadProgress({ current: 0, total: 0 });

    // The rejections used to be dropped with the allSettled results, so a driver
    // drove off believing every photo was saved. Never silent again.
    if (failed > 0) {
      const detail = describeUploadFailures(failed, failures);
      setUploadError(succeeded === 0 ? `Geen enkele foto is bewaard — ${detail}` : detail);
    }
  };

  const handleDelete = async (key: string) => {
    try {
      await deleteEasykitPhoto(taskId, key);
      setPhotos((prev) => prev.filter((p) => p.key !== key));
    } catch {
      // Error handling
    }
  };

  // PhotoLightbox is an image viewer; a PDF opens in a new tab instead, so it
  // must not appear in the lightbox array or in its index math.
  const imagePhotos = photos.filter((p) => !isPdfPhoto(p));

  if (!task) {
    return (
      <div className="px-4 py-8 max-w-lg mx-auto text-center">
        <p className="text-td-muted">Taak laden...</p>
      </div>
    );
  }

  const color = TYPE_COLORS[task.taskType] || TYPE_COLORS.other;

  return (
    <div className="px-4 py-5 max-w-lg mx-auto">
      {/* Back button */}
      <button
        type="button"
        onClick={() => {
          if (window.history.state?.idx > 0) {
            navigate(-1);
          } else {
            navigate('/easykit/driver');
          }
        }}
        className="flex items-center gap-1 text-sm text-td-blue hover:underline mb-4"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Terug
      </button>

      {/* Task info card */}
      <div className={`rounded-xl border border-gray-200 ${color.bg} border-l-4 ${color.border} p-5 mb-6`}>
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          {TYPE_PILL[task.taskType] && (
            <span className={`shrink-0 text-[11px] font-bold px-2 py-0.5 rounded ${TYPE_PILL[task.taskType]!.cls}`}>
              {TYPE_PILL[task.taskType]!.label}
            </span>
          )}
          {task.prefix && (
            <span className="shrink-0 text-xs font-bold text-amber-800 bg-amber-100 px-2 py-0.5 rounded truncate max-w-[60%]">
              {task.prefix}
            </span>
          )}
          <h1 className="text-lg font-bold text-td-navy">
            {task.taskType === 'druktest' && task.soSummary
              ? `${task.soSummary.collectors} ${task.soSummary.collectors === 1 ? 'collector' : 'collectoren'}`
              : (task.soSummary?.headline ?? task.name)}
          </h1>
        </div>
        {task.projectName && (
          <p className="text-sm text-td-navy/70 mt-1">{task.projectName}</p>
        )}
        {task.soSummary && task.soSummary.breakdown.length > 0 && (
          <div className="mt-3 rounded-lg border border-gray-200 bg-white/60 overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  <th className="text-left px-3 py-1.5 font-semibold">Systeem</th>
                  <th className="text-left px-3 py-1.5 font-semibold">Familie</th>
                  <th className="text-left px-3 py-1.5 font-semibold">LP</th>
                  <th className="text-right px-3 py-1.5 font-semibold">m²</th>
                </tr>
              </thead>
              <tbody>
                {task.soSummary.breakdown.map((c, i) => (
                  <tr key={i} className="border-t border-gray-100">
                    <td className="px-3 py-1.5 font-medium text-td-navy">{c.systeem}</td>
                    <td className="px-3 py-1.5 text-td-muted">{c.family}</td>
                    <td className="px-3 py-1.5 text-td-muted">{c.lp}</td>
                    <td className="px-3 py-1.5 text-right font-medium text-td-navy">{c.m2}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {task.address && (
          <p className="text-sm text-td-muted mt-2 flex items-center gap-1.5">
            <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            {task.address}
          </p>
        )}
        {task.tagIds && task.tagIds.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {task.tagIds.map((id) => {
              const cfg = TAG_CONFIG[id];
              if (!cfg) return null;
              return (
                <span key={id} className={`text-[11px] font-medium px-2.5 py-0.5 rounded-full ${cfg.cls}`}>
                  {cfg.label}
                </span>
              );
            })}
          </div>
        )}
        {task.description && (
          <p className="text-sm text-td-muted mt-3 whitespace-pre-wrap">{task.description}</p>
        )}
      </div>

      {/* Photos section */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-bold text-td-navy">Foto&apos;s</h2>
          <span className="text-xs text-td-muted">
            {photos.length} foto{photos.length !== 1 ? "'s" : ''}
          </span>
        </div>

        <StackedPhotoUploadSplitControls
          uploading={uploading}
          uploadProgress={uploadProgress}
          onFilesSelected={handleUpload}
        />

        {uploadError && (
          <p role="alert" className="text-xs text-red-600 mb-3">{uploadError}</p>
        )}

        {/* Photo grid — loading skeleton */}
        {photosLoading && photos.length === 0 && (
          <div className="grid grid-cols-3 gap-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="aspect-square bg-gray-100 rounded-lg animate-pulse" />
            ))}
          </div>
        )}

        {/* Photo grid */}
        {photos.length > 0 && (
          <div className="grid grid-cols-3 gap-2">
            {photos.map((photo) => (
              <div key={photo.key} className="relative group aspect-square">
                <PhotoGridThumb
                  photo={photo}
                  tileSizeClass="w-full h-full"
                  imgClassName="w-full h-full object-cover rounded-lg"
                  onOpen={() => setLightboxIndex(imagePhotos.findIndex((p) => p.key === photo.key))}
                />
                <button
                  type="button"
                  onClick={() => handleDelete(photo.key)}
                  className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-label="Verwijderen"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Empty state */}
        {!photosLoading && photos.length === 0 && (
          <p className="text-center text-sm text-td-muted py-6">Nog geen foto&apos;s</p>
        )}
      </div>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <PhotoLightbox
          photos={imagePhotos.map(taskPhotoToPhotoItem)}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </div>
  );
}
